import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { AxiosError, AxiosResponse } from 'axios';
import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';

import { FetchUserRoleParam, PutUserRoleParam, UpdateMyPageDataRequest, UpdateStreamDataRequest } from '@/Interfaces/I_userRole';

import { fetchUserRoleApi, putUserRoleApi, getMyPageUserProfileData, updateMyPageUserProfileData, getUserStreamerData, updateUserStreamerData } from '@/Axios/api/userRole';

import 
  { getUserRole, 
    putUserRole, 
    setUserRole, 
    spliceUserRole, 
    getStreamerData,
    setViewingStreamerData,
    updateStreamerData, 
    getMyPageProfileData,
    setMyPageProfileData,
    updateMyPageProfileData,
  } from '../slices/UserRoleSlice';
import { MyPageProfileData, StreamerData } from '@/Interfaces/I_AccountsManagement';

function* handleGetUserRole(action: PayloadAction<FetchUserRoleParam>) {
  try {
    const data: AxiosResponse = yield call(fetchUserRoleApi, action.payload);
    yield put(setUserRole(data));
  } catch (e) {
    // error
  }
}

function* handlePutUserRole(action: PayloadAction<PutUserRoleParam[]>) {
  try {
    yield call(putUserRoleApi, action.payload);
    message.success({
      content: 'User account changed',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
    if (action.payload[0].kocUserRole === "CUSTOMER") {
      yield put(spliceUserRole(action.payload))
    }
  } catch (e) {
    const err = e as AxiosError;
    message.error({
      content: err.response?.data.message,
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
  }
}

function* handleGetStreamerData(action: PayloadAction<string>) {
  try {
    const data: AxiosResponse<StreamerData> = yield call(getUserStreamerData, action.payload);
    if (data.status === 200) {
      yield put(setViewingStreamerData(data.data));
    }
  } catch (e) {
    message.error({
      content: 'Fail to get streamer data.',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
  }
}

function* handleUpdateStreamerData(action: PayloadAction<UpdateStreamDataRequest>) {
  try {
    const formData = new FormData();

    const data = {
      uuid: action.payload.uuid,
      name: action.payload.name,
      description: action.payload.description,
      isBannerDelete: action.payload.isBannerDelete,
      snsPlatforms: action.payload.snsPlatforms,
    };
    formData.append('data', new Blob([JSON.stringify(data)], { type: 'application/json' }));

    if (action.payload.banner) {
      formData.append('file', action.payload.banner);
    }

    const response: AxiosResponse<StreamerData> = yield call(updateUserStreamerData, formData);
    if (response.status === 200) {
      yield put(setViewingStreamerData(response.data));

      message.success({
        content: 'Stream data updated.',
        duration: 3,
        style: {
          marginTop: '120px',
        },
      });
    }
  } catch (e) {
    message.error({
      content: 'Fail to update streamer data.',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
  }
}

function* handleGetMyPageProfileData(action: PayloadAction<string>) {
  try {
    const data: AxiosResponse<MyPageProfileData> = yield call(getMyPageUserProfileData, action.payload);
    if (data.status === 200) {
      yield put(setMyPageProfileData(data.data));
    }
  } catch (e) {
    message.error({
      content: 'Fail to get my page profile data.',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
  }
}

function* handleUpdateMyPageProfileData(action: PayloadAction<UpdateMyPageDataRequest>) {
  try {
    const response: AxiosResponse<UpdateMyPageDataRequest> = yield call(updateMyPageUserProfileData, action.payload);

    if (response.status === 200) {
      message.success({
        content: 'my page profile data updated.',
        duration: 3,
        style: {
          marginTop: '120px',
        },
      });
    }
  } catch (e) {
    message.error({
      content: 'Fail to update my page profile data.',
      duration: 3,
      style: {
        marginTop: '120px',
      },
    });
  }
}

export function* watchGetUserRole() {
  yield takeLatest(getUserRole.type, handleGetUserRole);
}

export function* watchPutUserRole() {
  yield takeEvery(putUserRole.type, handlePutUserRole);
}

export function* watchUpdateStreamerData() {
  yield takeLatest(updateStreamerData.type, handleUpdateStreamerData);
}

export function* watchGetStreamerData() {
  yield takeLatest(getStreamerData.type, handleGetStreamerData);
}

export function* watchGetMyPageProfileData() {
  yield takeLatest(getMyPageProfileData.type, handleGetMyPageProfileData);
}

export function* watchUpdateMyPageProfileData() {
  yield takeLatest(updateMyPageProfileData.type, handleUpdateMyPageProfileData);
}

